import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import {
  AlertTriangle,
  Download,
  Loader2,
  RefreshCw,
  ShieldAlert,
  Clock,
  ShieldCheck,
} from 'lucide-react';
import { getApiBase } from './apiBase';

const API = axios.create({
  baseURL: getApiBase(),
  withCredentials: true,
  timeout: 180000,
});

function AnomalyBadge({ flag, reasons }) {
  if (!flag) {
    return (
      <span className="inline-flex rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">
        Normal
      </span>
    );
  }
  return (
    <span
      className="inline-flex flex-col gap-0.5 rounded-md border border-amber-300 bg-amber-50 px-2 py-1 text-[11px] text-amber-900"
      title={(reasons || []).join(', ')}
    >
      <span className="font-semibold">Anomaly</span>
      {(reasons || []).map((r) => (
        <span key={r} className="text-amber-800">
          {r}
        </span>
      ))}
    </span>
  );
}

export default function AuditThreatDashboard() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState('');
  const [pollResult, setPollResult] = useState(null);
  const [anomalyOnly, setAnomalyOnly] = useState(false);
  const [hours, setHours] = useState(48);
  const [timezone, setTimezone] = useState('');
  const [pollSource, setPollSource] = useState('hcm_sensitive');
  const [masking, setMasking] = useState(false);
  const [maskingResult, setMaskingResult] = useState(null);
  const [pollStatus, setPollStatus] = useState(null);

  useEffect(() => {
    API.get('/api/security/audit-alerts/poll-status', { validateStatus: () => true }).then((res) => {
      if (res.status === 200) setPollStatus(res.data);
    });
  }, []);

  const loadAlerts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await API.get('/api/security/audit-alerts', {
        params: { anomalyOnly, hours },
        validateStatus: () => true,
      });
      if (res.status === 200) {
        setAlerts(res.data.alerts || []);
        setTimezone(res.data.timezone || '');
      } else {
        setError(res.data?.error || `HTTP ${res.status}`);
        setAlerts([]);
      }
    } catch (err) {
      setError(err.message || 'Failed to load alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [anomalyOnly, hours]);

  useEffect(() => {
    loadAlerts();
  }, [loadAlerts]);

  async function handleValidateMasking() {
    setMasking(true);
    setError('');
    setMaskingResult(null);
    try {
      const res = await API.post('/api/security/validate-masking', {}, { validateStatus: () => true });
      if (res.status === 200) {
        setMaskingResult(res.data);
      } else {
        setError(res.data?.error || `Masking validation failed (${res.status})`);
      }
    } catch (err) {
      setError(err.message || 'Masking validation failed');
    } finally {
      setMasking(false);
    }
  }

  async function handlePoll() {
    setPolling(true);
    setError('');
    setPollResult(null);
    try {
      const res = await API.post(
        '/api/security/audit-alerts/poll',
        { hours, source: pollSource },
        { validateStatus: () => true }
      );
      if (res.status === 200) {
        setPollResult(res.data);
        await loadAlerts();
      } else {
        setError(res.data?.error || res.data?.message || `Poll failed (${res.status})`);
      }
    } catch (err) {
      setError(err.message || 'Poll failed');
    } finally {
      setPolling(false);
    }
  }

  function exportUrl() {
    const base = getApiBase().replace(/\/$/, '');
    const q = new URLSearchParams({
      anomalyOnly: anomalyOnly ? 'true' : 'false',
      hours: String(hours),
    });
    return `${base}/api/security/audit-alerts/export.xlsx?${q}`;
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <header className="flex flex-wrap items-start gap-4">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-gradient-to-br from-rose-500 to-amber-600 text-white shadow">
          <ShieldAlert className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold text-slate-900">Audit threat dashboard</h1>
          {timezone && (
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              Anomaly timezone: {timezone}
            </p>
          )}
          {pollStatus && (
            <p className="text-xs text-slate-500 mt-1">
              Scheduled poll: {pollStatus.cronEnabled ? `enabled (${pollStatus.cronSchedule})` : 'disabled via env'}{' '}
              · Last poll:{' '}
              {pollStatus.lastPollAt ? new Date(pollStatus.lastPollAt).toLocaleString() : 'never'}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={handlePoll}
            disabled={polling}
            className="inline-flex items-center gap-2 rounded-lg bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
          >
            {polling ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Poll now
          </button>
          <a
            href={exportUrl()}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
          >
            <Download className="h-4 w-4" />
            Export Excel
          </a>
          <button
            type="button"
            onClick={handleValidateMasking}
            disabled={masking}
            className="inline-flex items-center gap-2 rounded-lg border border-violet-300 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-900 hover:bg-violet-100 disabled:opacity-50"
          >
            {masking ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
            Validate masking
          </button>
        </div>
      </header>

      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-slate-200 bg-white px-4 py-3 text-sm">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={anomalyOnly}
            onChange={(e) => setAnomalyOnly(e.target.checked)}
            className="rounded border-slate-300"
          />
          Anomalies only
        </label>
        <label className="inline-flex items-center gap-2">
          Hours
          <select
            value={hours}
            onChange={(e) => setHours(Number(e.target.value))}
            className="rounded border border-slate-300 px-2 py-1"
          >
            <option value={24}>24</option>
            <option value={48}>48</option>
            <option value={72}>72</option>
          </select>
        </label>
        <label className="inline-flex items-center gap-2">
          Source
          <select
            value={pollSource}
            onChange={(e) => setPollSource(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1"
          >
            <option value="hcm_sensitive">HCM PER audit (PER tables)</option>
            <option value="bip_fnd">FND BIP report</option>
            <option value="fusion_db">Fusion DB (ATP)</option>
          </select>
          <span className="text-[10px] text-slate-500">PER = default HCM tables; FND = connection BIP path</span>
        </label>
        <button
          type="button"
          onClick={loadAlerts}
          className="text-cyan-700 hover:text-cyan-900 text-xs font-medium"
        >
          Refresh list
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800 flex gap-2">
          <AlertTriangle className="h-5 w-5 shrink-0" />
          {error}
        </div>
      )}

      {pollResult?.ok && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          Poll complete: {pollResult.inserted} rows ({pollResult.anomalyCount} anomalies) via{' '}
          {pollResult.auditSource || pollResult.source}.
        </div>
      )}

      {maskingResult && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            maskingResult.productionWarning
              ? 'border-amber-200 bg-amber-50 text-amber-900'
              : maskingResult.compliant
                ? 'border-emerald-200 bg-emerald-50 text-emerald-900'
                : 'border-rose-200 bg-rose-50 text-rose-900'
          }`}
        >
          <p className="font-medium">{maskingResult.summary}</p>
          {maskingResult.environmentType && (
            <p className="text-xs mt-1 opacity-80">
              Environment: {maskingResult.environmentType} · {maskingResult.connectionLabel}
            </p>
          )}
        </div>
      )}

      {maskingResult?.checks?.length > 0 && (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">Table</th>
                <th className="px-3 py-2">Field</th>
                <th className="px-3 py-2">Sample</th>
                <th className="px-3 py-2">Passed</th>
                <th className="px-3 py-2">Failed</th>
                <th className="px-3 py-2">Risk</th>
                <th className="px-3 py-2">Examples</th>
              </tr>
            </thead>
            <tbody>
              {maskingResult.checks.map((c) => (
                <tr key={`${c.table}-${c.field}`} className="border-b border-slate-100">
                  <td className="px-3 py-2 text-xs">{c.table}</td>
                  <td className="px-3 py-2 text-xs font-mono">{c.field}</td>
                  <td className="px-3 py-2">{c.sampleSize}</td>
                  <td className="px-3 py-2 text-emerald-700">{c.passed}</td>
                  <td className="px-3 py-2 text-rose-700">{c.failed}</td>
                  <td className="px-3 py-2 text-xs">{c.riskLevel}</td>
                  <td className="px-3 py-2 text-xs max-w-xs truncate" title={(c.examples || []).join(', ')}>
                    {(c.examples || []).join(', ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin" />
          Loading alerts…
        </div>
      ) : !alerts.length ? (
        <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-600">
          No alerts in the last {hours} hours.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-3 py-2">Event</th>
                <th className="px-3 py-2">Actor</th>
                <th className="px-3 py-2">Employee</th>
                <th className="px-3 py-2">Object</th>
                <th className="px-3 py-2">Summary</th>
                <th className="px-3 py-2">Risk</th>
              </tr>
            </thead>
            <tbody>
              {alerts.map((a) => (
                <tr key={a.id} className="border-b border-slate-100 hover:bg-slate-50/80">
                  <td className="px-3 py-2 whitespace-nowrap text-xs text-slate-600">
                    {a.eventAt ? new Date(a.eventAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{a.actor || '—'}</td>
                  <td className="px-3 py-2">
                    <div className="font-medium text-slate-900">{a.employeeName || '—'}</div>
                    <div className="text-xs text-slate-500">
                      {a.employeePersonNumber || a.employeePersonId}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div>{a.tableName}</div>
                    {a.attributeName && (
                      <div className="text-slate-500">{a.attributeName}</div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs max-w-xs truncate" title={a.changeSummary}>
                    {a.changeSummary}
                  </td>
                  <td className="px-3 py-2">
                    <AnomalyBadge flag={a.anomalyFlag} reasons={a.anomalyReasons} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
